import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";

import { KNOT, knotGeometry, prefersReducedMotion } from "../../lib/knot";
import { MASK, useMaskId } from "./Knot";

/** One tie and untie, in ms. */
export const LOADER_CYCLE = 2000;

const N = 3;
const r3 = (v: number) => Math.round(v * 1000) / 1000;
// The knot drawn tight, as in the logo, on a cord a little longer than it.
const TIED = knotGeometry({ n: N, x0: 8, x1: 56 });

// A turn's pose, from where it sits tight, for a knot `c` tight (0 loose,
// 1 tight, more pulled past it): loose, a turn sits further out by the
// difference in pitch for each place it's out, and leans further by the
// difference in slant, a skew about the cord.
const pose = (i: number, c: number) => {
  const lean = ((KNOT.slantLoose - KNOT.slantTight) / KNOT.h) * (1 - c);
  const out = (i - (N - 1) / 2) * (KNOT.pitchLoose - KNOT.pitchTight);
  return {
    dx: r3(out * (1 - c) - 32 * lean),
    skew: r3((Math.atan(lean) * 180) / Math.PI),
  };
};

// The motion, as [when in the cycle, how tight, easing into the next]: loose
// while the turns wind on; pulled tight, a little past and back; held;
// loosened to come undone.
const LINEAR = "0 0 1 1";
const TIGHTEN: [number, number, string][] = [
  [0, 0, LINEAR],
  [0.3, 0, "0.2 0.6 0.4 1"],
  [0.4, 1.25, "0.4 0 0.6 1"],
  [0.46, 1, LINEAR],
  [0.62, 1, "0.4 0 0.6 1"],
  [0.7, 0, LINEAR],
  [1, 0, LINEAR],
];
// When each turn winds on and off: one at a time (the ellipsis of a
// "Loading…"), the last on the first off.
const WIND = (i: number) => [
  0.1 * i,
  0.1 * (i + 1),
  0.91 - 0.07 * (i + 1),
  0.91 - 0.07 * i,
];
// Just past 1: a dash of nothing would still draw its round cap.
const OFF = 1.02;

const keys = (times: number[]) => times.map(r3).join(";");
const cycle = {
  dur: `${LOADER_CYCLE}ms`,
  repeatCount: "indefinite",
};

const Motion: React.FC<{ i: number }> = ({ i }) => {
  const [on, wound, off, gone] = WIND(i);
  const poses = TIGHTEN.map(([, c]) => pose(i, c));
  const tightening = {
    ...cycle,
    attributeName: "transform",
    keyTimes: keys(TIGHTEN.map(([t]) => t)),
    calcMode: "spline",
    keySplines: TIGHTEN.slice(0, -1)
      .map(([, , ease]) => ease)
      .join(";"),
  };
  return (
    <>
      <animate
        {...cycle}
        attributeName="stroke-dashoffset"
        values={[OFF, OFF, 0, 0, OFF, OFF].join(";")}
        keyTimes={keys([0, on, wound, off, gone, 1])}
      />
      <animateTransform
        {...tightening}
        type="translate"
        values={poses.map((p) => p.dx).join(";")}
      />
      <animateTransform
        {...tightening}
        type="skewX"
        additive="sum"
        values={poses.map((p) => p.skew).join(";")}
      />
    </>
  );
};

type LoaderProps = {
  /** What's under way, read out in place of the knot. */
  label?: string;
  /** A page's or a panel's: centred, with room of its own, in crimson. */
  block?: boolean;
};

/**
 * Something's loading: the long knot winds on a turn at a time, pulls
 * tight, comes undone and ties again. SVG's own animation moves it, as CSS
 * can't move what a mask cuts, so nothing re-renders and no timer runs. With
 * reduced motion it keeps still, loose with two turns of three wound on.
 */
export const Loader: React.FC<LoaderProps> = ({
  label = "Loading…",
  block = false,
}) => {
  const svg = useRef<SVGSVGElement>(null);
  const maskId = useMaskId();
  const [still] = useState(prefersReducedMotion);
  // Every loader keeps the page's time, so one that takes over from another
  // (the route's, then the page's) carries on tying the same knot.
  useLayoutEffect(() => {
    const el = svg.current!;
    // jsdom keeps no SMIL clock.
    if (!still && el.setCurrentTime)
      el.setCurrentTime((performance.now() % LOADER_CYCLE) / 1000);
  }, [still]);
  const turns = still ? TIED.turns.slice(0, N - 1) : TIED.turns;
  const turn = (i: number) =>
    still
      ? {
          transform: `translate(${pose(i, 0).dx} 0) skewX(${pose(i, 0).skew})`,
        }
      : { strokeDasharray: "1 2" };
  return (
    <output className={block ? "loader loader-block" : "loader"}>
      <svg
        ref={svg}
        className="knot loader-knot"
        viewBox="4 18 56 28"
        aria-hidden="true"
        focusable="false"
      >
        <mask id={maskId} maskUnits="userSpaceOnUse" {...MASK}>
          <rect {...MASK} fill="#fff" />
          {turns.map((t, i) => (
            <path
              key={i}
              d={t.d}
              stroke="#000"
              strokeWidth={TIED.cw}
              pathLength={1}
              {...turn(i)}
            >
              {!still && <Motion i={i} />}
            </path>
          ))}
        </mask>
        <path
          className="knot-cord"
          d={TIED.cord}
          strokeWidth={TIED.w}
          mask={`url(#${maskId})`}
        />
        {turns.map((t, i) => (
          <path
            key={i}
            className="knot-turn"
            d={t.d}
            strokeWidth={TIED.tw}
            pathLength={1}
            {...turn(i)}
          >
            {!still && <Motion i={i} />}
          </path>
        ))}
      </svg>
      <span className="sr-only">{label}</span>
    </output>
  );
};

/**
 * A button's label, or while it works the loader in its place. The label
 * stays, unseen, so the button keeps its width.
 */
export const Busy: React.FC<{
  busy: boolean;
  /** What it's doing: "Saving…". */
  label: string;
  children: React.ReactNode;
}> = ({ busy, label, children }) =>
  busy ? (
    <span className="busy">
      <span className="busy-label" aria-hidden="true">
        {children}
      </span>
      <Loader label={label} />
    </span>
  ) : (
    <>{children}</>
  );
